import {Component, Input, OnInit} from '@angular/core';
import {ColorScheme} from '@lib/models/color-scheme/color-scheme';
import {EntityType} from '@lib/models/entity/entity-type';
import {NewsSectionLayout} from '@lib-modules/section/components/news-section/news-section.component';
import {INewsPost} from '@api/models/news/news';
import {LeagueService} from '@core/services/league.service';
import {TeamService} from '@core/services/team.service';
import {MatchSectionLayout} from '@lib-modules/section/components/match-section/match-section.component';
import {AppLeague} from '@shared/models/league';
import {EventCardService, EventContentType} from '@lib-modules/cards/services/event-card/event-card.service';
import {PickerItem, PickerItemType} from '@lib-modules/picker/components/picker-item/picker-item.component';
import {Event} from '@api/models/event/event';
import {AdCampaignService} from '@components/ad-campaign/services/ad-campaign.service';
import {AdCampaignPlacement} from '@api/models/ad-campaign/ad-campaign-placement';
import {NewsCardService, NewsContentType} from '@lib-modules/cards/services/news-card/news-card.service';
import {VideoCardService, VideoContentType} from '@lib-modules/cards/services/video-card/video-card.service';
import {IUser} from '@api/models/user/user';
import {StateService} from '@app/core/services/state.service';
import {LeagueType} from '@api/models/league/league-type';

@Component({
  selector: 'lib-entity-home-section',
  templateUrl: './entity-home-section.component.html',
  styleUrls: ['./entity-home-section.component.scss']
})
export class EntityHomeSectionComponent implements OnInit {

  protected readonly EntityType = EntityType;
  @Input() type: EntityType;
  @Input() colorScheme = ColorScheme.Dark;
  @Input() user: IUser;
  readonly NewsLayout = NewsSectionLayout;
  readonly MatchLayout = MatchSectionLayout;
  public entityId: string;
  public leagueForTable: AppLeague;
  public livestreams: Event[] = [];
  public replays: Event[] = [];
  public events: Event[] = [];
  public editorialNews: INewsPost[] = [];
  public videoNews: INewsPost[] = [];
  public news: INewsPost[] = [];
  public otherNews: INewsPost[] = [];
  public topTeams: PickerItem[];
  public selectedItem: PickerItem;
  public isLoading = false;
  public showVideoContent = true;
  public entityType = EntityType;

  constructor(
    public leagueService: LeagueService,
    public teamService: TeamService,
    private eventCardService: EventCardService,
    private newsCardService: NewsCardService,
    private videoCardService: VideoCardService,
    private adCampaignService: AdCampaignService,
  ) {
  }

  async ngOnInit() {
    this.adCampaignService.setPlacement(AdCampaignPlacement.Home);
    switch (this.type) {
      case EntityType.Team:
        this.entityId = this.teamService.getCurrentTeamId();
        const teamItem = {
          objectId: this.teamService.getCurrentTeamId(),
          name: this.teamService.getCurrentTeam().name,
          readableId: this.teamService.getCurrentTeam().readable_id,
          type: PickerItemType.Team
        }
        await this.onItemSelect(teamItem)
        break;
      case EntityType.League:
        this.loadTopTeamsForLeague();
        break;
    }
  }

  private loadTopTeamsForLeague() {
    if (this.topTeams) {
      return;
    }
    this.entityId = this.leagueService.getCurrentLeagueId();
    this.topTeams = this.leagueService.getCurrentTopTeamsAsPickerItems();
  }

  async onItemSelect(selectedItem: PickerItem) {
    this.isLoading = true;
    this.selectedItem = selectedItem;
    this.showVideoContent = await this.videoCardService.showVideoContent(selectedItem);

    if (this.showVideoContent) {
      this.editorialNews = await this.newsCardService.fetchData(NewsContentType.EntityNews, selectedItem, 0, 4, { editorial: true});
      this.livestreams = await this.videoCardService.fetchData(VideoContentType.LiveStreams, selectedItem);
      this.replays = await this.videoCardService.fetchData(VideoContentType.Replay, selectedItem);
    }

    await this.getEvents();
    this.videoNews = await this.newsCardService.fetchData(NewsContentType.VideoNews, selectedItem);
    const allNews = await this.newsCardService.fetchData(NewsContentType.EntityNews, selectedItem, 0, 16)
    this.news = allNews.slice(0, 4);
    this.otherNews = allNews.slice(4, 16);
    await this.setLeagueForTable();
    this.isLoading = false
  }

  private async getEvents() {
    let events = [];
    events = await this.eventCardService.fetchData(
      EventContentType.EventsByDate, this.selectedItem, 0, 16, new Date()
    );
    if (events.length < 16) {
      const limit = this.type === EntityType.Team ? 1 : 4;
      const upcomingEvents = await this.eventCardService.fetchData(
        EventContentType.UpcomingEvents, this.selectedItem, 0, limit,
      );
      if (!StateService.isBrowser) {
        upcomingEvents.reverse()
      }
      upcomingEvents.forEach(event => {
        if (!events.find(e => e._id === event._id)) {
          events.push(event);
        }
      });
    }
    if (events.length < 16) {
      const pastEvents = await this.eventCardService.fetchData(
        EventContentType.PastEvents, this.selectedItem, 0, 16,
      );
      pastEvents.forEach(event => {
        if (!events.find(e => e._id === event._id)) {
          events.push(event);
        }
      });
    }
    this.events = events.slice(0, 16);
  }

  private async setLeagueForTable() {
    switch (this.type) {
      case EntityType.League:
        // Only set table in leagues with league type
        if (this.leagueService.currentLeague.leagueType === LeagueType.League) {
          this.leagueForTable = this.leagueService.currentLeague;
        }
        break;
      case EntityType.Team:
        const leagueId = this.teamService.currentTeam.getLeagueForTable();
        if (leagueId) {
          this.leagueForTable = await this.leagueService.getLeague(leagueId);
        }
        break;
    }
  }

  public get bannerName(): string {
    switch (this.type) {
      case EntityType.League:
        return this.leagueService.currentLeague.name;
      case EntityType.Team:
        return this.selectedItem.name;
    }
  }
}
